// The gesture owns the camera (SPEC-viewfinder §1.1, §1.2, §1.3, §1.7, ADR-0018).
//
// Authority is inverted here. Until Decision 47 the state drove the camera:
// `paint()` read `viewState`, derived a zoom from the radius and wrote
// `map.easeTo`. Now the finger drives it and the camera settles into the state
// — once, when the gesture is finished.
//
// **One write per gesture.** Not one per frame. `paint` is both the camera
// authority and a `derived` subscriber, so a per-frame `viewState.set` would be
// one push per frame and therefore one camera write per frame — `easeTo`
// restarting under the finger (#238, relaying #244 S7b's review of PR #274).
// A `'replace'` write per frame would also fold the whole gesture into
// `carried` and ride onto the next Back (#278 is fixed and does not make that
// safe). So: nothing reaches `viewState` until the camera is at rest.
//
// **What DOES run per frame** is `onFrame` — the slider, its value and the
// radius bands — and `./live-frame.ts`'s own measurement of distances and
// membership. Neither goes through `derived.subscribe`: that seam stays neutral
// under a gesture (§1.1, rule 1) and `src/derived.ts` learns nothing of a camera.
//
// **Finished** means every input released PLUS the end of any glide (§1.7): a
// `pointerdown` landing mid-glide continues the SAME gesture, so a flick caught
// and flicked on is one Back, not two.
import type { Map as MlMap } from 'maplibre-gl';

import { resolveCentre } from '../state/centre.ts';
import { clampCentre, insideBounds } from '../state/clamp.ts';
import { moveCentre } from '../state/commands.ts';
import { viewState } from '../state/viewstate.ts';
import { hearArrows } from './arrows.ts';
import { hearDoubleTap } from './double-tap.ts';
import { endGesture, liveFrame, startGesture } from './live-frame.ts';
import { relayFromMarks } from './mark-drag.ts';
import { MAX_RADIUS, MIN_RADIUS, radiusForZoom, zoomFor } from './wheel-geometry.ts';

/** `paint()`'s own tolerance for „the camera is already there" (`wheel.ts`, the prototype's app.js:156). */
const SAME_PLACE = 1e-6;

/** What the wheel needs from the module that owns its camera. */
export type CameraOwner = {
  /**
   * Whether a gesture owns the map right now. `camera()` is skipped while it
   * does, and `chrome()` is not — a radius changed by a pinch must still move
   * the slider under the finger (§1.1).
   *
   * **This flag is the suppression, and it lives here** — on the wheel's side
   * of the seam, never in `src/derived.ts` (ADR-0018).
   */
  owns(): boolean;
  /**
   * Whether the camera has moved under the pointer sequence that is ending, so
   * the click it produces is swallowed: one gesture, one result (D-d). A flag
   * cleared by the next `pointerdown` and NEVER a timer — a swallow window eats
   * the tap that follows it, which is the bug the long press had before it.
   */
  dragged(): boolean;
  /** Re-apply the handler set and the zoom stops: both are derived from the measured disc (§1.2). */
  refresh(): void;
  /**
   * Run a camera write of OUR OWN — `camera()`'s ease, a `map.resize()` — so
   * the move it causes is not read as a gesture.
   *
   * It has to be marked rather than recognised. MapLibre gives `originalEvent`
   * on a drag's `movestart` and NOT on a scroll zoom's (measured on 6.10.0:
   * `movestart`/`zoomstart` both come through with none), so a camera move is
   * distinguished by who asked for it and by nothing else.
   */
  own(write: () => void): void;
};

/** What the owner needs from the wheel. Everything measured is a function, never a number. */
export type CameraDeps = {
  map: MlMap;
  /**
   * The map BLOCK — the `region` named „Co jest blisko", not the canvas. It
   * carries the tab stop §0.2 inserts and the arrow keys that stop is for, so
   * they inherit its name (§1.7). The canvas container, where a pointer lands
   * first, is MapLibre's own and is asked of the map.
   */
  block: HTMLElement;
  /**
   * The layer the marks are drawn in. It is the ONE thing on the overlay that
   * takes a pointer, so it is also the one thing that can swallow a drag
   * (#304) — {@link relayFromMarks} gives the swallowed ones back.
   */
  marks: HTMLElement;
  /** The disc's pixel diameter, `0` before the first measurement. */
  disc: () => number;
  /** Whether gestures are offered at all: no in the dial, no with a failed basemap (§6). */
  offered: () => boolean;
  /** `prefers-reduced-motion: reduce` — no glide (§1.2). */
  reduced: () => boolean;
  /** Run on every gesture frame with the radius the camera now means (§1.1). */
  onFrame: (radius: number) => void;
};

/**
 * Everything the owner remembers between events. A record rather than five
 * closure variables, so the wiring below can live outside the factory and stay
 * under the size limits of ADR-0015.
 */
export type Live = {
  /** A gesture owns the camera, from its first frame until the camera is at rest. */
  gesturing: boolean;
  /** Pointers down on the map. A `moveend` with one down is a re-grab, not a settle. */
  pointers: number;
  /**
   * Arrow keys held on the block, beside {@link pointers} and for the same
   * reason: a held key repeats, each repeat's `panBy` raises its own `moveend`,
   * and §1.7 asks for one entry per press. `./arrows.ts` fills it.
   */
  keys: Set<string>;
  /** Did the camera move under this pointer sequence? The click swallow reads it. */
  dragged: boolean;
  /**
   * A camera write of ours is running RIGHT NOW, synchronously — every event it
   * raises inside the call belongs to it. The clamp's `setCenter` uses it too.
   */
  owning: boolean;
  /** A camera write of ours whose EASE is still running, after the call returned. */
  ours: boolean;
  /**
   * The camera's zoom when this gesture took the map, or `null` between
   * gestures. It is the ONLY thing that says whether the radius changed, and it
   * is the zoom rather than the derived radius: `radiusForZoom` divides by
   * `cos(lat)`, so the same zoom at a different latitude is a different radius,
   * and ADR-0009's 10 m rounding makes the difference visible. Measured on the
   * phone's 287 px disc, zoom held exactly: r = 2810 at Stary Rynek is 2800 a
   * kilometre north, so a run of pure pans walked `r` up and down the address —
   * which §1.1 forbids, a pan writing `{ centre, at: null }` and nothing else.
   */
  startZoom: number | null;
};

export function ownCamera(deps: CameraDeps): CameraOwner {
  const { map } = deps;
  const live: Live = {
    gesturing: false,
    pointers: 0,
    keys: new Set(),
    dragged: false,
    owning: false,
    ours: false,
    startZoom: null,
  };

  /**
   * {@link CameraOwner.own}, and the two flags are not one flag.
   *
   * `easeTo` and `jumpTo` both call `map.stop()` FIRST, so a write of ours
   * raises a `moveend` for whatever was running and THEN a `movestart` for
   * itself, both inside this call. With a single flag the `moveend` consumed it
   * and the `movestart` a line later read as the resident's: `camera()` was
   * then suppressed as if a gesture owned the map, and the next settle wrote
   * the stale camera back over the state. That is measured, not hypothetical —
   * it turned a granted position into a picked „wybrane miejsce".
   *
   * So `owning` swallows everything raised INSIDE the call, and `ours` carries
   * the ease that outlives it, until its own `moveend`.
   */
  function own(write: () => void) {
    live.ours = true;
    live.owning = true;
    try {
      write();
    } finally {
      live.owning = false;
    }
    // A write that moved nothing is over here; there is no `moveend` to come.
    if (!map.isMoving()) live.ours = false;
  }

  hearPointers(deps, live);
  hearCamera(deps, live);
  relayFromMarks(deps, live);
  hearDoubleTap(deps);
  const arrows = hearArrows(deps, live, () => finish(deps, live));

  return {
    owns: () => live.gesturing,
    dragged: () => live.dragged,
    refresh: () => {
      own(() => offer(map, deps.disc(), deps.offered() && deps.disc() > 0, deps.reduced()));
      // The tab stop is part of the handler set: §6 takes the arrow keys away
      // exactly where it takes the drag away.
      arrows.refresh();
    },
    own,
  };
}

/** Who has a finger on the map, and when a gesture is over. */
function hearPointers(deps: CameraDeps, live: Live) {
  const container = deps.map.getCanvasContainer();
  // Capture phase, and that is load-bearing: a `pointerdown` landing mid-glide
  // makes MapLibre call `map.stop()`, which fires `moveend` inside this very
  // event. The count has to be up by then or the re-grab reads as a settle and
  // the flick becomes two Backs instead of one (§1.7).
  container.addEventListener(
    'pointerdown',
    () => {
      live.pointers += 1;
      // A finger on the map ends any ease of OURS: MapLibre calls `map.stop()`
      // and takes over, so the camera from here is the resident's. Without this
      // the first drag after a `◌`, a pasted link or a slider step landed
      // inside our own 350 ms ease, fired no `movestart` of its own — the
      // camera was already moving — and settled nothing at all.
      live.ours = false;
    },
    true,
  );
  // The swallow is cleared on ANY pointer going down, not only on one the map
  // hears: a tap that lands straight on a mark never reaches the canvas, and a
  // flag only the canvas could clear would eat that tap for ever.
  window.addEventListener('pointerdown', () => (live.dragged = false), true);
  // On the window, because a finger may lift outside the block — and a lift we
  // never hear would leave the count up and the gesture never settling.
  for (const type of ['pointerup', 'pointercancel'] as const) {
    window.addEventListener(
      type,
      () => {
        live.pointers = Math.max(0, live.pointers - 1);
        // A lift is the OTHER way a gesture finishes, and it is not redundant:
        // the clamp's `setCenter` is a `jumpTo`, which calls `map.stop()` and
        // ends MapLibre's own move sequence — so a drag held against the city's
        // edge lifts with no `moveend` left to come, and without this the whole
        // gesture wrote nothing. When a glide IS coming the map is still moving
        // here, and the glide's own `moveend` finishes it instead.
        finish(deps, live);
      },
      true,
    );
  }
  // A wheel over the map ends any ease of ours too: the resident is driving
  // now, and the `movestart` that follows must be read as theirs.
  container.addEventListener('wheel', () => (live.ours = false), true);
}

/** Any camera move this module did not ask for is a gesture. That is the whole rule. */
function hearCamera(deps: CameraDeps, live: Live) {
  const { map } = deps;
  map.on('movestart', () => {
    if (live.owning || live.ours) return;
    if (!live.gesturing) startGesture(map, live);
    live.gesturing = true;
  });
  map.on('move', () => {
    if (live.owning || !live.gesturing) return;
    live.dragged = true;
    // The clamp is a write of ours in the middle of the resident's: `setCenter`
    // is a `jumpTo`, so it raises its own move events and they are not a
    // gesture. It does NOT touch `ours` — the gesture's own `moveend` is what
    // has to finish it.
    live.owning = true;
    holdInside(map);
    live.owning = false;
    // The slider follows the camera's ZOOM and nothing else. A pan holds the
    // zoom exactly, so a pan moves no slider — without this guard the same
    // `cos(lat)` drift that leaked into the address walked the strip's value
    // under the finger.
    const disc = deps.disc();
    if (disc > 0 && zoomed(live, map.getZoom())) {
      deps.onFrame(radiusForZoom(map.getZoom(), disc, map.getCenter().lat));
    }
    // Distances, membership and entering marks, throttled to one frame (§4.1).
    liveFrame(deps);
  });
  map.on('moveend', () => {
    if (live.owning) return;
    // Our own write is done; the next move that is not ours is a gesture.
    if (live.ours) {
      live.ours = false;
      return;
    }
    finish(deps, live);
  });
}

/**
 * The gesture is finished — every input released AND the camera at rest (§1.7)
 * — so it writes its one entry. A `pointerdown` landing mid-glide keeps
 * `pointers` up and a held arrow keeps `keys` up, which is why a flick caught
 * and flicked on, and a held `→`, are each one Back and not two.
 */
function finish(deps: CameraDeps, live: Live) {
  if (!live.gesturing || live.pointers > 0 || live.keys.size > 0 || deps.map.isMoving()) return;
  live.gesturing = false;
  // Before the write, so the one derivation it causes finds no gesture in
  // flight and the rail rebuilds exactly once, with the FLIP it earns (§4.2).
  endGesture();
  settle(deps.map, deps.disc(), zoomed(live, deps.map.getZoom()));
  live.startZoom = null;
}

/**
 * Did THIS gesture change the zoom? MapLibre holds the zoom bit-exactly through
 * a pan, so the comparison needs no tolerance of its own; the epsilon is there
 * for the ease that settles a pinch, which lands a float's width away.
 */
function zoomed(live: Live, now: number): boolean {
  return live.startZoom === null || Math.abs(now - live.startZoom) > 1e-9;
}

/**
 * The hard stop at the city's edge, applied on the FRAME rather than on settle:
 * „the drag stops" has to be visible under the finger, and a camera clamped
 * only at rest would overshoot and snap back (§1.3). Setting the centre
 * re-enters `move` exactly once with a value already inside the box, so it
 * terminates. No bounce and no message — the drag simply stops.
 */
function holdInside(map: MlMap) {
  const at = map.getCenter();
  const point = { lat: at.lat, lon: at.lng };
  if (insideBounds(point)) return;
  const inside = clampCentre(point);
  map.setCenter([inside.lon, inside.lat]);
}

/**
 * One write, and exactly one, for the whole gesture (§4.2).
 *
 * The centre is read off the CAMERA and compared against the centre the state
 * stands at **now** — never one remembered at `pointerdown`. #279 made that
 * concrete: a refused permission clears `here` and calls every listener in the
 * same tick, so a `tu=ja` centre can move while a gesture is in flight, and a
 * gesture owner that wrote back what it started from would silently undo it.
 */
function settle(map: MlMap, disc: number, movedRadius: boolean) {
  if (disc === 0) return;
  const at = map.getCenter();
  const centre = clampCentre({ lat: at.lat, lon: at.lng });
  const view = viewState.get();
  const from = resolveCentre(view);
  const movedCentre =
    Math.abs(centre.lat - from.lat) > SAME_PLACE || Math.abs(centre.lon - from.lon) > SAME_PLACE;
  // Read only where it is going to be written: deriving it on a pan would hand
  // `moveCentre` a radius the resident never asked for (see `Live.startZoom`).
  const radius = movedRadius ? radiusForZoom(map.getZoom(), disc, centre.lat) : view.radius;
  // A centre move is an opening, and a gesture that changed both writes ONE
  // `'open'` — the centre moved, and one finished gesture is one Back (§1.7).
  // A radius that changed alone is `'replace'`: a control that changes the view
  // without opening anything (ADR-0009), exactly as the slider is.
  const changed = movedRadius && radius !== view.radius;
  // The place name is `moveCentre`'s (§1.5, #312): it names every centre move
  // the same way, and doing it there rather than here is what a settled pan, the
  // empty-ring sentence's tap and a `sel=` beyond the ring now share. It is still
  // a scan of 71 centroids on SETTLE and never per frame, because this is the one
  // place a gesture writes.
  if (movedCentre) moveCentre(centre, null, changed ? { radius } : {});
  else if (changed) viewState.set({ radius }, 'replace');
}

/**
 * Which handlers are on, and where the zoom stops are. Re-asked, never set once.
 *
 * Exported for `camera.test.ts` alone, and for a reason the review found: with
 * the two stops dropped, every E2E scenario stayed green, because
 * `radiusForZoom` clamps `r` to 200–5 000 anyway and the address never sees it.
 * What the address cannot see is that DURING a gesture the camera's zoom is
 * then free while `chrome()` draws the rings at the old radius.
 */
export function offer(map: MlMap, disc: number, offered: boolean, reduced: boolean) {
  if (offered) {
    // Inertia is MapLibre's own and it is kept — except under `reduce`, where a
    // top speed of zero leaves the camera exactly where the finger left it. §8
    // of SPEC-redesign removes motion rather than shortening it (#233).
    map.dragPan.enable(reduced ? { maxSpeed: 0 } : undefined);
    // Around the CENTRE, and only this one. MapLibre zooms around the cursor by
    // default, which pans the centre — so a roll of the wheel wrote a centre
    // and therefore a history entry, where §7.3 says it must write none. A
    // pinch pans around its two-finger midpoint and a double tap recentres on
    // the tap point, both by the owner's ruling (§1.2), so those keep it.
    map.scrollZoom.enable({ around: 'center' });
    map.touchZoomRotate.enable();
  } else {
    map.dragPan.disable();
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();
  }
  // Never, in any state: the rings, the district names and the mark projection
  // all assume north-up, and a rotated basemap under fixed rings is a different
  // product (§1.2).
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.keyboard.disable();
  // Never MapLibre's: it zooms a whole level, which is HALF the radius, and
  // ruling 10 is one step on the slider's own scale (`./double-tap.ts`).
  map.doubleClickZoom.disable();
  if (disc === 0) return;
  // Zoom IS radius, so the range's ends ARE the zoom stops: a pinch past either
  // of them stops at it and feels stopped rather than frozen (§1.2). Both are
  // derived from the MEASURED disc, which is why this is re-applied rather than
  // set once at construction.
  const { lat } = map.getCenter();
  map.setMinZoom(zoomFor(MAX_RADIUS, disc, lat));
  map.setMaxZoom(zoomFor(MIN_RADIUS, disc, lat));
}
